import { SHEET_DISPLAY_NAMES, type SheetType } from '../models/sheet-types';
import {
  resolveFieldLinkTarget,
  type FieldLinkUi,
} from './field-link-navigation.util';
import { type FieldLinkTooltip } from './field-link-tooltip.model';

import role9Mfrs from '../../assets/forms/role-210100-mfrs.form.json';
import role9Mpers from '../../assets/forms/role-210100-mpers.form.json';
import role13Mfrs from '../../assets/forms/role-310100-mfrs.form.json';
import role13Mpers from '../../assets/forms/role-310100-mpers.form.json';
import role15Mfrs from '../../assets/forms/role-410000-mfrs.form.json';
import role15Mpers from '../../assets/forms/role-410000-mpers.form.json';

/**
 * Human-readable tooltip copy for a field-link anchor — the clickable row label
 * on the Statement of Financial Position and Statement of Profit or Loss that
 * jumps to the field it inherits its value from.
 *
 * WHY THIS EXISTS — the tooltip used to read `Open ifrs-full:LongtermBorrowings`.
 * The qname is precise but it is taxonomy jargon, and a preparer reading it
 * still cannot tell *which sheet* the link goes to. Worse, the DESTINATION's
 * label is usually not the source's: SOFP's "Borrowings" links to
 * "Total non-current borrowings" on Sub-classification, and SOFP has TWO
 * "Borrowings" rows (non-current / current) whose only visible difference used
 * to be the qname. So the tooltip names the destination field and its sheet:
 *
 *     Open "Total non-current borrowings" in Sub-classification of Assets, …
 *
 * SEPARATE FILE, on purpose — `field-link-navigation.util.ts` resolves the
 * navigation target and is imported by seven sheet components; it stays free of
 * schema imports so nothing pays for the label tables unless it wants the copy.
 */

/** Minimal structural view of a form schema — the JSON is far wider. */
interface LabelledNode {
  qname?: unknown;
  label?: unknown;
}

/**
 * Every `qname -> label` pair anywhere in a schema. Controls nest under sections
 * on some sheets, so this walks the whole tree rather than reading `.controls`.
 */
function collectLabels(json: unknown, into: Map<string, string>): Map<string, string> {
  const walk = (node: unknown): void => {
    if (Array.isArray(node)) {
      node.forEach(walk);
      return;
    }
    if (node && typeof node === 'object') {
      const rec = node as LabelledNode & Record<string, unknown>;
      if (typeof rec.qname === 'string' && typeof rec.label === 'string')
        into.set(rec.qname, rec.label);
      Object.values(rec).forEach(walk);
    }
  };
  walk(json);
  return into;
}

/**
 * Destination sheet -> its fields' labels, MFRS and MPERS merged into one table.
 *
 * Merging the two taxonomies is safe and deliberately avoids plumbing a
 * taxonomy argument through seven components (each of which detects its own
 * taxonomy differently, so a wrong answer would be a silent mislabel). It holds
 * because the two variants' qname sets are almost disjoint — one shared qname on
 * Sub-classification, one on Comprehensive Income — and both shared qnames carry
 * IDENTICAL labels, so no lookup is ambiguous. `field-link-label.util.spec.ts`
 * asserts that across all three destinations; if a future schema edit gives one
 * shared qname two different labels, that spec fails and the merge has to become
 * taxonomy-aware.
 *
 * Only the three sheets any `ui.linkTo` actually targets are listed. Roles 9 and
 * 13 are already statically imported by their pure-validator rules files, so
 * they cost nothing extra here; role 15 (410000) is the only new bundle entry.
 */
const LABELS_BY_DESTINATION: Readonly<Partial<Record<SheetType, Map<string, string>>>> = {
  'sub-classification': collectLabels(role9Mpers, collectLabels(role9Mfrs, new Map())),
  'analysis-of-profit-or-loss': collectLabels(
    role13Mpers,
    collectLabels(role13Mfrs, new Map())
  ),
  'statement-of-comprehensive-income': collectLabels(
    role15Mpers,
    collectLabels(role15Mfrs, new Map())
  ),
};

/**
 * The destination field's own label, or null when the qname is not declared on
 * that sheet (a rotted `ui.linkTo`, or a destination with no label table above).
 * `field-link-integrity.spec.ts` pins that every shipped link resolves.
 */
export function resolveFieldLinkLabel(
  sheet: SheetType,
  qname: string
): string | null {
  return LABELS_BY_DESTINATION[sheet]?.get(qname) ?? null;
}

/**
 * Structured tooltip for a field-link anchor, or null when the control is not
 * linkable (the caller renders plain text with no anchor and no tooltip).
 *
 * `ownLabel` is the FALLBACK, used when the destination's label cannot be
 * resolved — pass the control's RAW `label`, never the `| negativeLabel` output.
 *
 * `details` is deliberately unset today: the shipped copy is one line. It exists
 * so a second line becomes a change here rather than in seven sheets.
 */
export function fieldLinkTooltipModel(
  ui: FieldLinkUi | null | undefined,
  ownQname: string,
  ownLabel: string | null | undefined
): FieldLinkTooltip | null {
  const target = resolveFieldLinkTarget(ui, ownQname);
  if (!target) return null;

  const field =
    resolveFieldLinkLabel(target.sheet, target.scrollTo) ||
    ownLabel?.trim() ||
    target.scrollTo;
  const sheetName = SHEET_DISPLAY_NAMES[target.sheet];

  return { headline: sheetName ? `Open "${field}" in ${sheetName}` : `Open "${field}"` };
}
